import React from 'react';
import TextField from '@material-ui/core/TextField';
import CustomGreenSwitch from 'app/components/CustomGreenSwitch';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import { Editor } from '@tinymce/tinymce-react';
import { TEMPLATE_VARIABLES } from 'app/constants/formFields';
import { recipientsError } from 'app/main/utils/emailRecipients';

const SEND_TO_HINT =
  "One address, or several separated by commas. Leave it empty when this template is used as a form's submission confirmation — those always go to the address the submitter entered.";

function TemplateForm({ form, setForm }) {
  const sendToError = recipientsError(form.sendTo);

  const handleChange = e => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
  };

  return (
    <div className="flex flex-1 justify-center mt-20 mb-20">
      <div className="w-full max-w-xl mx-auto p-24 flex flex-col gap-24">
        <TextField
          required
          label="Template name"
          name="name"
          value={form.name}
          onChange={handleChange}
          variant="outlined"
          fullWidth
        />

        <TextField
          label="Send to"
          name="sendTo"
          value={form.sendTo}
          onChange={handleChange}
          variant="outlined"
          fullWidth
          error={Boolean(sendToError)}
          helperText={sendToError || SEND_TO_HINT}
        />

        <TextField
          required
          label="Subject"
          name="subject"
          value={form.subject}
          onChange={handleChange}
          variant="outlined"
          fullWidth
        />

        <div>
          <p
            style={{
              marginBottom: 8,
              color: 'rgba(0,0,0,0.85)',
              fontSize: '10.5px',
              lineHeight: 1,
              marginLeft: 15,
              fontFamily: 'Arial, sans-serif'
            }}
          >
            Email body
          </p>
          <Editor
            apiKey={process.env.REACT_APP_TINYMCE_API_KEY}
            value={form.emailBody}
            onEditorChange={content => setForm({ ...form, emailBody: content })}
            init={{
              height: 400,
              menubar: false,
              plugins: 'lists link media',
              toolbar: [
                'undo redo | blocks fontfamily fontsize | bold italic underline forecolor | alignmenu bullist numlist | removeformat | link | addmedia | variables'
              ],
              content_style: 'body { font-family: Arial, sans-serif; font-size: 14px; }',
              setup: editor => {
                editor.ui.registry.addButton('addmedia', {
                  icon: 'embed',
                  text: 'Add Media',
                  tooltip: 'Insert media',
                  onAction: () => editor.execCommand('mceMedia')
                });

                editor.ui.registry.addMenuButton('alignmenu', {
                  icon: 'align-left',
                  tooltip: 'Alignment',
                  fetch: callback => {
                    callback([
                      {
                        type: 'menuitem',
                        text: 'Align Left',
                        icon: 'align-left',
                        onAction: () => editor.execCommand('JustifyLeft')
                      },
                      {
                        type: 'menuitem',
                        text: 'Align Center',
                        icon: 'align-center',
                        onAction: () => editor.execCommand('JustifyCenter')
                      },
                      {
                        type: 'menuitem',
                        text: 'Align Right',
                        icon: 'align-right',
                        onAction: () => editor.execCommand('JustifyRight')
                      },
                      {
                        type: 'menuitem',
                        text: 'Justify',
                        icon: 'align-justify',
                        onAction: () => editor.execCommand('JustifyFull')
                      }
                    ]);
                  }
                });

                editor.ui.registry.addMenuButton('variables', {
                  text: 'Insert Variable',
                  tooltip: 'Insert a dynamic variable into the template',
                  fetch: callback => {
                    const items = TEMPLATE_VARIABLES.map(v => ({
                      type: 'menuitem',
                      text: `${v.label}  ${v.value}`,
                      onAction: () => editor.insertContent(v.value)
                    }));
                    callback(items);
                  }
                });
              }
            }}
          />
        </div>

        <FormControlLabel
          control={
            <CustomGreenSwitch
              checked={Boolean(form.active)}
              onChange={e => setForm({ ...form, active: e.target.checked })}
            />
          }
          label={form.active ? 'Active' : 'Inactive'}
        />
      </div>
    </div>
  );
}

export default TemplateForm;
